/** @jsx jsx */

import { Box, Flexbox, Row, Text } from '@chameleoncreator/creator-ui';
import { css, jsx } from '@emotion/core';
import Arrow from 'components/UI/Arrow';
import { keyframes } from 'emotion';
import { ThemeProvider } from 'emotion-theming';
import { motion } from 'framer-motion';
import React, {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import hexToRgb, { getHexToRgbWithDefaultFallback } from 'services/hexToRGB';
import isAndroidSamsung from 'services/isAndroidSamsung';
import isAppleComputerInc from 'services/isAppleComputerInc';
import unlockInteraction from 'services/unlockInteraction';
import updateReadOnlyText from 'services/updateReadOnlyText';
import { State } from 'store/store';
import { interactionContext as InteractionContext } from './interactionContext';
import { HostingContext } from 'hosting/context';
import { accordionOpen, accordionClose } from 'hosting/events';

const fader = keyframes`
from {
  opacity: 0;
}
to {
  opacity: 1;
}
`;

//
const defaultBorderRadius = 6;
const highContrastBorderThickness = 4;

// Close all the accordions (used when e)
const close = (obj: {}): {} => {
  return Object.keys(obj).reduce((a, k) => {
    a[k] = false;
    return a;
  }, {});
};

const clearFieldHilites = () => {
  const fieldsClosed = Array.from(
    document.querySelectorAll('.accordion-field') as NodeList
  ) as HTMLElement[];

  fieldsClosed.forEach(field => {
    field.style.boxShadow = `none`;
  });
};

// The useAccordion hook is a state object that allows
// for accordions open/close state to be tracked and altered.
// If the exclusive tag is passed in, it will close all the
// fields when one is openned
const useAccordion = (
  unlock: () => void,
  count: number,
  exclusive?: boolean
) => {
  const [state, setState] = useState({
    open: {},
    viewed: {},
  });

  const toggle = (key: string) => {
    const past = exclusive ? close(state.open) : { ...state.open };
    const viewed = state.viewed[key.toString()]
      ? state.viewed
      : { ...state.viewed, [key.toString()]: true };

    if (Object.keys(viewed).length === count) {
      unlock();
    }

    setState({
      ...state,
      viewed,
      open: { ...past, [key]: !state.open[key] },
    });
  };

  const isOpen = (key: string) => {
    return state.open[key] || false;
  };

  return { toggle, isOpen };
};

interface AccordionCtx {
  toggle(key: string): void;
  isOpen(key: string): boolean;
}

const AccordionContext = createContext<AccordionCtx>({
  toggle: () => {},
  isOpen: () => false,
});

type IsAppleAndroid = {
  id: string;
  isOpen: boolean;
  borderRadius: number;
  fieldBodyTextColor: string;
  children: React.ReactElement;
};

const IsAppleAndroid = (props: IsAppleAndroid) => {
  const { id, isOpen, borderRadius, fieldBodyTextColor, children } = props;

  useLayoutEffect(() => {
    const rowFieldBody = document.querySelector(`#i${id}`);
    rowFieldBody && updateReadOnlyText(rowFieldBody);
  }, [isOpen, fieldBodyTextColor]);

  return (
    <Row
      id={`i${id}`}
      className={isOpen ? 'fader_1s' : ''}
      css={css`
        height: ${isOpen ? 'auto' : 0};
        margin: 0 !important;
        padding: 0 !important;
        border-radius: 0 0 ${borderRadius}px ${borderRadius}px;

        p,
        li > span {
          color: ${fieldBodyTextColor};
        }

        overflow: hidden;
      `}
      aria-hidden={!isOpen}
      aria-live="assertive"
    >
      {children}
    </Row>
  );
};

// A field is the grouping of headers and bodies of accordion fields.
// It passes information down the the header / body to toggle and
// open status
export const Field = ({ children, ...props }) => {
  const interactionContext = useContext(InteractionContext);
  const hostingCtx = useContext(HostingContext);
  // console.log({ hostingCtx });

  const { toggle, isOpen } = useContext(AccordionContext);
  const {
    headingColor,
    borderColor,
    includeBorder = true,
    borderRadius = defaultBorderRadius,
    includeFieldBodyBackground = true,
    fieldBodyBackgroundOpacity = 100,
    fieldBodyBackgroundColor = '#fff',
  } = interactionContext.props;

  const { id, idx } = props;

  const trigger = () => {
    toggle(idx);
  };

  const interactionIsOpen = useSelector((state: State) => {
    return state.interactionsOpen.includes(interactionContext.id);
  });

  const isFieldOpen = isOpen(idx);

  const { r, g, b } = getHexToRgbWithDefaultFallback(
    fieldBodyBackgroundColor,
    '#ffffff'
  );

  let a = fieldBodyBackgroundOpacity / 100;
  if (!fieldBodyBackgroundColor) {
    a = 0;
  }

  useEffect(() => {
    const onFocus = () => {
      const { activeElement } = document;

      // used for when we tab out of the accordion 'boundary'
      // if the element with focus is not a fieldHeader
      // clearFieldHilites
      if (!activeElement?.className.match('FieldHeader')) {
        clearFieldHilites();
      }
    };

    document.addEventListener('focus', onFocus, true);

    return () => {
      document.removeEventListener('focus', onFocus, true);
    };
  }, []);

  const fieldBody = useRef<HTMLDivElement>(null);
  const headerHeight = useRef<number>(0);

  const now = useRef<number | null>(Date.now());

  useLayoutEffect(() => {
    if (!fieldBody.current) {
      return;
    }

    const header = fieldBody.current.querySelector(
      '[class*="FieldHeader"]'
    ) as HTMLElement;
    headerHeight.current = header.getBoundingClientRect().height;
  }, [interactionIsOpen, isFieldOpen]);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    if (interactionContext?.id !== window['sectionInView']?.id) {
      // console.log(interactionContext?.id, window['sectionInView']?.id);
      return;
    }

    if (isFieldOpen) {
      hostingCtx?.data(
        accordionOpen(interactionContext.id, { accordion_id: id })
      );

      return;
    }

    if (now.current) {
      hostingCtx?.data(
        accordionClose(interactionContext.id, {
          accordion_id: id,
          duration: Date.now() - now.current,
          // / 1000,
        })
      );

      now.current = Date.now();
    }
  }, [isFieldOpen]);

  return (
    <Box
      ref={fieldBody}
      className={`accordion-field ${isFieldOpen ? 'accordion-field-open' : ''}`}
      mt={1}
      mb={1}
      css={css({
        position: 'relative',
        border: includeBorder
          ? `1px solid ${borderColor || headingColor || '#222'}`
          : 'none',
        borderRadius,
      })}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          ...(headerHeight.current && {
            background: includeFieldBodyBackground
              ? `rgba(${r}, ${g}, ${b}, ${a})` || fieldBodyBackgroundColor
              : 'transparent',
            borderRadius,

            clipPath: `polygon(0 ${headerHeight.current}px, 100% ${headerHeight.current}px, 100% 100%, 0 100%)`,
          }),
        }}
      />
      <div
        style={{
          position: 'relative',
        }}
      >
        {React.Children.map(children, child =>
          cloneElement(child, { trigger, isOpen: isFieldOpen })
        )}
      </div>
    </Box>
  );
};

interface HeaderProps {
  children: JSX.Element | string;
  foreground?: string;
  background?: string;
}

interface InjectdProps {
  isOpen: boolean;
  trigger(key: string): void;
}

// The Field Header is
export const FieldHeader = ({
  children,
  foreground = '#fff',
  background = '#222',
  ...props
}: HeaderProps) => {
  const { isOpen, trigger } = props as InjectdProps;

  const {
    includeHeadingBackground = true,
    headingBackgroundOpacity = 100,
    headingColor,
    headingTextColor,
    includeBorder = true,
    borderRadius = defaultBorderRadius,
  } = useContext(InteractionContext).props;

  const highContrast = useSelector((state: State) => {
    return state.theme.highContrast;
  });

  const fieldHeaderRef = useRef<HTMLElement>(null);

  const fieldFauxFocus = `0 0 0 1px #fff, 0 0 0 ${
    highContrast ? highContrastBorderThickness : 2
  }px ${headingColor}`;

  const { r, g, b } = hexToRgb(headingColor || background);
  const a = headingBackgroundOpacity / 100;

  //
  const borderRadiusRealculatedForCSSBug =
    borderRadius + (includeBorder ? -1 : 0);

  return (
    <ThemeProvider theme={{ Text: { color: headingTextColor || foreground } }}>
      <Flexbox
        ref={fieldHeaderRef}
        as="button"
        border="none"
        flexDirection="row"
        alignItems="center"
        css={css`
          position: relative;
          // left: -1px;
          // width: calc(100% + 2px);
          width: 100%;

          background: ${includeHeadingBackground
            ? `rgba(${r}, ${g}, ${b}, ${a})` || headingColor || background
            : 'transparent'};

          .chameleon-text {
            padding: 10px ${window.dev() ? '5px' : '7px'};
          }

          border-radius: ${isOpen
            ? `${borderRadiusRealculatedForCSSBug}px ${borderRadiusRealculatedForCSSBug}px 0 0`
            : `${borderRadiusRealculatedForCSSBug}px`};

          transition: border-radius ${0.225 / 1.5}s;

          &:focus-visible {
            box-shadow: ${!isOpen ? fieldFauxFocus : 'none'};
          }

          ${!isOpen &&
            `
          &:focus {
            // box-shadow: ${fieldFauxFocus};
          }

          &:focus-visible {
            // box-shadow: ${fieldFauxFocus};
          }

          `}

          cursor: pointer;
        `}
        onFocus={e => {
          clearFieldHilites();

          if (isOpen) {
            const fieldOpen = document.querySelector(
              '.accordion-field-open'
            ) as HTMLElement;

            if (fieldOpen) {
              //
              fieldOpen.style.boxShadow = fieldFauxFocus;
              //
            }
          }
        }}
        onClick={e => {
          trigger('');
        }}
        onKeyUp={e => {
          if (window.dev()) {
            e.preventDefault();
            return;
          }

          if (e.keyCode === 32) {
            e.currentTarget.click();
          }
        }}
        p={2}
        aria-expanded={isOpen}
      >
        <Text
          as="div"
          flex="1"
          textAlign="left"
          css={css`
            * div {
              color: ${headingTextColor};
            }

            p {
              // line-height: auto !important;
            }
          `}
        >
          {children}
        </Text>
        <Box flex={0} p={1} pl={3} pr={2}>
          <Arrow
            color={headingTextColor || '#fff'}
            rotate={isOpen ? 90 : 0}
            delay={isOpen ? 0.25 : 0}
          />
        </Box>
      </Flexbox>
    </ThemeProvider>
  );
};

// This posed element is responsible for the transition between open and closed states
// const Collapse = posed.div({
//   closed: { height: 0 },
//   open: { height: 'auto' },
// });

export const FieldBody = ({ children, ...props }) => {
  const { isOpen } = props as InjectdProps;
  const { id } = props;

  const fieldBody = useRef<HTMLDivElement>(null);

  const { fieldBodyTextColor, borderRadius = 0 } = useContext(
    InteractionContext
  ).props;

  //
  // var it for testing . . .
  const isDevice = isAppleComputerInc() || isAndroidSamsung();

  useEffect(() => {
    if (!fieldBody.current || !isOpen) {
      return;
    }

    // .chameleon-text,
    const fieldBodyElements = Array.from(
      fieldBody.current.querySelectorAll(
        'img, .video-player-container, [class*="Audio"] img, audio, svg'
      )
    ) as HTMLElement[];

    if (!fieldBodyElements?.length) {
      return;
    }

    fieldBodyElements.forEach(entry => {
      entry.setAttribute('tabIndex', '0');
    });

    fieldBodyElements[0].focus();
  }, [isOpen]);

  if (window.dev && !window.dev() && isDevice) {
    return (
      <IsAppleAndroid
        key={`${id}-${isOpen}`}
        {...{
          id,
          isOpen,
          borderRadius,
          fieldBodyTextColor,
        }}
      >
        {children}
      </IsAppleAndroid>
    );
  }

  return (
    <motion.div
      ref={fieldBody}
      id={`i${id}`}
      style={{
        display: 'none',
        overflow: 'hidden',
        borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
      }}
      initial={{
        height: 0,
        opacity: 0,
      }}
      transition={{
        duration: 0.225,
        ease: 'easeOut',
      }}
      animate={{
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0,
        //
      }}
      onAnimationStart={() => {
        if (isOpen && fieldBody.current) {
          fieldBody.current.style.display = '';
        }
      }}
      onAnimationComplete={() => {
        if (!isOpen && fieldBody.current) {
          fieldBody.current.style.display = 'none';
        }
      }}
      aria-hidden={!isOpen}
      aria-live="assertive"
    >
      <Row
        css={css`
          p,
          li > span {
            color: ${fieldBodyTextColor};
          }
        `}
      >
        {children}
      </Row>
    </motion.div>
  );
};

// An accordion is made up of 1 or more children
interface AccordionProps {
  children: JSX.Element[] | JSX.Element;
}

export const Accordion = ({ children }: AccordionProps) => {
  const iContext = useContext(InteractionContext);
  const interactionsLocked = useSelector((state: State) => {
    return state.interactionsLocked;
  });

  // made this tweak here to fix the issue anglia revealed
  // problem is i'm kinda sure this was different code at some earlier point
  // works now. all good. but might want to chat with sam
  // . . .
  const unlock = () => {
    if (window.dev && window.dev()) {
      return;
    }

    if (!interactionsLocked?.includes(iContext.id)) {
      return;
    }

    unlockInteraction(iContext.id);
  };

  const ctx = useAccordion(unlock, React.Children.count(children), true);

  return (
    <AccordionContext.Provider value={{ ...ctx }}>
      {React.Children.map(children, (c, i) => cloneElement(c, { idx: i }))}
    </AccordionContext.Provider>
  );
};
